import { useState } from 'react';
import cx from 'classnames';
import { Clock, Grid2x2, List, RefreshCw } from 'lucide-react';
import { Loading } from '../Loading';
import { DeviceListTable } from './DeviceListTable';
import { DeviceListGrid } from './DeviceListGrid';
import type { DeviceListProps } from './types';
import { observer } from 'mobx-react-lite';
import { Button } from '../Button';

const defaultT = (key: string) => key;

/**
 * The app locale enums spell some tags with an underscore (zh_CN), which
 * Intl rejects outright, so normalise and then fall back to the browser
 * locale rather than let a clock label throw the list away.
 */
const formatTime = (timestamp: number, locale?: string) => {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  const date = new Date(timestamp);
  try {
    return date.toLocaleTimeString(locale?.replace('_', '-'), options);
  } catch {
    return date.toLocaleTimeString(undefined, options);
  }
};

/**
 * DeviceList - Unified device list/table component
 *
 * Supports dual view modes (card/list), expandable accordions, sortable columns,
 * and app-specific expansion content via children render prop.
 */
export const DeviceList: React.FC<DeviceListProps> = observer(
  ({
    app,
    devices,
    viewMode = 'list',
    onViewModeChange,
    expandable = false,
    expandedIds: controlledExpandedIds,
    onExpand,
    sortable = false,
    sortKey,
    sortDir = 'asc',
    onSort,
    onClick,
    title,
    onRefresh,
    refreshing = false,
    lastUpdated,
    locale,
    loading = false,
    error,
    emptyMessage,
    t = defaultT,
    children,
    actions,
    toolbar,
    className,
  }) => {
    // Uncontrolled expansion state
    const [internalExpandedIds, setInternalExpandedIds] = useState<Set<string>>(
      new Set()
    );
    const expandedIds = controlledExpandedIds
      ? new Set(controlledExpandedIds)
      : internalExpandedIds;

    const handleToggleExpand = (deviceId: string) => {
      if (controlledExpandedIds) {
        // Controlled mode - call callback
        onExpand?.(deviceId);
      } else {
        // Uncontrolled mode - manage internally
        setInternalExpandedIds((prev) => {
          const next = new Set(prev);
          if (next.has(deviceId)) {
            next.delete(deviceId);
          } else {
            next.add(deviceId);
          }
          return next;
        });
        onExpand?.(deviceId);
      }
    };

    const updatedAt =
      lastUpdated != null ? formatTime(lastUpdated, locale) : null;

    const refreshButton = onRefresh ? (
      <Button
        onClick={onRefresh}
        variant="outline"
        disabled={refreshing}
        aria-label={t('Refresh')}
        title={t('Refresh')}
      >
        <RefreshCw className={cx('w-5 h-5', refreshing && 'animate-spin')} />
      </Button>
    ) : null;

    const handleViewModeChange = () => {
      const newMode = viewMode === 'list' ? 'card' : 'list';
      onViewModeChange?.(newMode);
    };

    // Loading state
    if (loading) {
      return (
        <div className={cx('space-y-6', className)}>
          <div className="flex justify-between items-center">
            {title && (
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {title}
                </h2>
              </div>
            )}
          </div>
          <div className="flex justify-center items-center min-h-[400px]">
            <Loading />
          </div>
        </div>
      );
    }

    // Empty state
    if (devices.length === 0) {
      return (
        <div className={cx('space-y-6', className)}>
          <div className="flex justify-between items-center">
            {title && (
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {title}
                </h2>
                <p className="mt-1 text-sm text-foreground-secondary">
                  0 {t('devices')}
                </p>
              </div>
            )}
            {(onViewModeChange || refreshButton || toolbar) && (
              <div className="flex gap-2">
                {toolbar}
                {refreshButton}
                {onViewModeChange && (
                <button
                  onClick={handleViewModeChange}
                  className={cx(
                    'p-2 rounded-lg transition-colors',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    viewMode === 'card' && 'bg-gray-200 dark:bg-gray-600'
                  )}
                  aria-label={
                    viewMode === 'card'
                      ? t('Switch to list view')
                      : t('Switch to card view')
                  }
                >
                  {viewMode === 'card' ? (
                    <Grid2x2 className="w-5 h-5" />
                  ) : (
                    <List className="w-5 h-5" />
                  )}
                </button>
                )}
              </div>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12">
            <p className="text-foreground-secondary text-center text-lg">
              {emptyMessage || t('No devices found')}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={cx('space-y-6', className)} data-component="DeviceList">
        {/* Error banner */}
        {error && (
          <div role="alert" className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <p className="text-red-800 dark:text-red-400">
              {t('Error')}: {error}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center">
          {title && (
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {title}
              </h2>
              <p className="mt-1 text-sm text-foreground-secondary">
                {devices.length}{' '}
                {devices.length === 1 ? t('device') : t('devices')}
                {updatedAt && (
                  // Clock glyph rather than a word, so the staleness cue needs
                  // no translation in any of the thirteen locales.
                  <span
                    className="ml-2 inline-flex items-center gap-1 align-middle"
                    title={t('Last updated')}
                  >
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                    <span className="sr-only">{t('Last updated')}</span>
                    {updatedAt}
                  </span>
                )}
              </p>
            </div>
          )}
          {(onViewModeChange || refreshButton || toolbar) && (
            <div className="flex gap-2">
              {toolbar}
              {refreshButton}
              {onViewModeChange && (
              <Button
                onClick={handleViewModeChange}
                variant="outline"
                // className={cx(
                //   'p-2 rounded-lg transition-colors',
                //   'hover:bg-gray-100 dark:hover:bg-gray-700',
                //   viewMode === 'card' && 'bg-gray-200 dark:bg-gray-600'
                // )}
                aria-label={
                  viewMode === 'card'
                    ? t('Switch to list view')
                    : t('Switch to card view')
                }
              >
                {viewMode === 'card' ? (
                  <Grid2x2 className="w-5 h-5" />
                ) : (
                  <List className="w-5 h-5" />
                )}
              </Button>
              )}
            </div>
          )}
        </div>

        {/* Device display */}
        {viewMode === 'list' ? (
          <DeviceListTable
            app={app}
            devices={devices}
            expandable={expandable}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            onClick={onClick}
            sortable={sortable}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            actions={actions}
            t={t}
          >
            {children}
          </DeviceListTable>
        ) : (
          <DeviceListGrid
            app={app}
            devices={devices}
            expandable={expandable}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            onClick={onClick}
            actions={actions}
            t={t}
          >
            {children}
          </DeviceListGrid>
        )}
      </div>
    );
  }
);
